import { useState, useRef, useEffect } from "react";
import emailjs from "emailjs-com";
import { FaPaperPlane, FaCheckCircle } from "react-icons/fa";

// Subscribers land in an Apps Script sheet, tagged so they can be filtered
// apart from the sales leads the corporate site's contact form sends there.
const GOOGLE_SHEETS_URL =
  "https://script.google.com/macros/s/AKfycbyZsPzIClTYsU7GWXDHbrbdobdpsNvJIVsqsaf4io12AOBivOTgSxNOwndzKdb_hTG9/exec";

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(""); // "success" | "error" | ""
  const statusTimer = useRef(null);

  useEffect(() => () => clearTimeout(statusTimer.current), []);

  // Both outcomes clear themselves, so a stale error can't sit under the form
  // for the rest of the visit.
  const flashStatus = (next) => {
    setStatus(next);
    clearTimeout(statusTimer.current);
    statusTimer.current = setTimeout(() => setStatus(""), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    clearTimeout(statusTimer.current);
    setStatus("");

    const timestamp = new Date().toLocaleString();
    // The sheet's columns come from the contact form on quantarock.in, so
    // `name` and `phone` are sent as fixed placeholders to keep rows aligned.
    const commonPayload = {
      name: "Newsletter Subscriber",
      email,
      phone: "-",
      message: "Newsletter signup (website footer)",
      timestamp,
    };

    try {
      // Was axios, which existed in the bundle for this one call. Two
      // differences to keep the behaviour identical: fetch resolves rather
      // than throws on a 4xx/5xx, so the status is checked explicitly, and it
      // hands back the raw body, which is trimmed before comparing — the
      // Apps Script replies with the bare word "Success".
      const url = new URL(GOOGLE_SHEETS_URL);
      Object.entries(commonPayload).forEach(([key, value]) =>
        url.searchParams.set(key, value)
      );

      const sheetResponse = await fetch(url, { method: "POST" });

      if (!sheetResponse.ok) {
        throw new Error(`Sheet responded ${sheetResponse.status}.`);
      }

      const sheetResult = (await sheetResponse.text()).trim();

      if (sheetResult !== "Success") {
        throw new Error("There was an error saving the subscription.");
      }

      // Best-effort email notification — the row is already saved either way.
      try {
        await emailjs.send(
          "service_bdjqp8n",
          "template_i3ztwcb",
          { ...commonPayload, reply_to: email },
          "8QM2nZ_tvq9ll0779"
        );
      } catch (notifyErr) {
        // Non-fatal: subscription is already recorded in the sheet.
      }

      flashStatus("success");
      setEmail("");
    } catch (err) {
      flashStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border-t border-b border-white/20 py-10 md:py-12 mb-8">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-white">
          Stay in the loop
        </h2>
        <p className="text-base text-[var(--website-text-color)] mt-2 mb-6">
          Get product updates and IT procurement tips — no spam.
        </p>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row items-stretch justify-center gap-3 max-w-lg mx-auto"
        >
          {/* The visible heading carries the context, so the label is for
              assistive tech only — the placeholder alone is not an accessible name. */}
          <label htmlFor="newsletter-email" className="sr-only">
            Your email address
          </label>
          <input
            id="newsletter-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            className="flex-1 px-5 py-3 rounded-lg text-base bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[var(--website-secondary-color)]"
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 rounded-lg font-semibold text-base flex items-center justify-center gap-2 bg-[var(--website-secondary-color)] text-[var(--website-primary-color)] hover:opacity-90 transition disabled:opacity-60 whitespace-nowrap"
          >
            {isSubmitting ? "Sending..." : "Subscribe"}
            <FaPaperPlane size={14} aria-hidden="true" />
          </button>
        </form>

        {/* The live region is always mounted so screen readers announce the
            result; swapping the whole node in would be missed. */}
        <div role="status" aria-live="polite">
          {status === "success" && (
            <div className="mt-4 text-sm text-[var(--website-secondary-color)] flex items-center justify-center gap-2">
              <FaCheckCircle aria-hidden="true" /> Thanks for subscribing!
            </div>
          )}
          {status === "error" && (
            <div className="mt-4 text-sm text-red-400">
              Something went wrong. Please try again.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Newsletter;
